import styled from "@emotion/styled";

export const Button = styled.button`
  display: inline-block;
  border: 1px solid ${({ theme }) => theme.colors.grey70};
  background: #fff;
  font-size: 1.4rem;
  border-radius: 4px;
  font-weight: 700;
  padding: 0.643em 1.5em;
  margin: 5px;
  font-family: ${({ theme }) => theme.fonts.sans};
  line-height: 1;
  outline: none;
  border-bottom-color: ${({ theme }) => theme.colors.grey60};
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.15);
  transition: background-color ${({ theme }) => theme.transitions.hover},
    border-color ${({ theme }) => theme.transitions.hover};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.grey90};
    cursor: pointer;
  }

  ${({ active }) =>
    active &&
    `
    background: #eeeeee;
    box-shadow: 0 1px 1px rgba(0, 0, 0, 0.15),
      0 3px 7px rgba(0, 0, 0, 0.15) inset;
  `}

  ${({ ghost, theme }) =>
    ghost &&
    `
    background: transparent;
    color: ${theme.colors.black};
    border: 1px solid ${theme.colors.black};
    box-shadow: none;

    &:hover {
      color: white;
      background: ${theme.colors.black};
    }

    &:active {
      background: ${theme.colors.grey20};
      border-color: ${theme.colors.grey20};
    }
  `}

  ${({ ghost, white, theme }) =>
    ghost &&
    white &&
    `
      color: white;
      border-color: white;
      &:hover {
        color: ${theme.colors.black};
        background: white;
      }

      &:active {
        background: ${theme.colors.grey85};
        border-color: ${theme.colors.grey85};
      }
  `}

  ${({ ghost, accent1, theme }) =>
    ghost &&
    accent1 &&
    `
      color: ${theme.colors.red};
      background: transparent;
      border-color: ${theme.colors.red};

      &:hover {
        color: white;
        background: ${theme.colors.red};
        border-color: ${theme.colors.red};
      }

      &:active {
        color: white;
        background: ${theme.colors.red35};
        border-color: ${theme.colors.red35};
      }
  `}

  ${({ ghost, accent2, theme }) =>
    ghost &&
    accent2 &&
    `
      color: ${theme.colors.blue};
      background: transparent;
      border-color: ${theme.colors.blue};
      &:hover {
        color: white;
        background: ${theme.colors.blue};
        border-color: ${theme.colors.blue};
      }

      &:active {
        color: white;
        background: ${theme.colors.blue35};
        border-color: ${theme.colors.blue35};
      }
  `}

  ${({ circle }) =>
    circle &&
    `
    width: 2.5em;
    height: 2.5em;
    padding: .75em;
    border-radius: 50%;
    line-height: 1;
  `}

  ${({ flat, theme }) =>
    flat &&
    `
    box-shadow: none;
    background: ${theme.colors.red};
    border-color: ${theme.colors.red};
    color: white;

    &:hover {
      background: ${theme.colors.red40};
      border-color: ${theme.colors.red40};
    }

    &:active {
      background: ${theme.colors.red35};
      border-color: ${theme.colors.red35};
    }
  `}

  ${({ flat, accent1, theme }) =>
    flat &&
    accent1 &&
    `
      background: ${theme.colors.red};
      border-color: ${theme.colors.red};

      &:hover {
        background: ${theme.colors.red40};
        border-color: ${theme.colors.red40};
      }

      &:active {
        background: ${theme.colors.red35};
        border-color: ${theme.colors.red35};
      }
  `}

  ${({ flat, accent2, theme }) =>
    flat &&
    accent2 &&
    `
      background: ${theme.colors.blue};
      border-color: ${theme.colors.blue};

      &:hover {
        background: ${theme.colors.blue40};
        border-color: ${theme.colors.blue40};
      }

      &:active {
        background: ${theme.colors.blue35};
        border-color: ${theme.colors.blue35};
      }
  `}

  ${({ accent1, theme }) =>
    accent1 &&
    `
    background: ${theme.colors.red};
    color: white;
    border-color: ${theme.colors.red35};
    border-bottom-color: ${theme.colors.red30};

    &:hover {
      background:  ${theme.colors.red40};
      border-bottom-color: ${theme.colors.red20};
    }

    &:active {
      background:  ${theme.colors.red35};
    }
  `}

  ${({ accent2, theme }) =>
    accent2 &&
    `
    background: ${theme.colors.blue};
    color: white;
    border-color: ${theme.colors.blue35};
    border-bottom-color: ${theme.colors.blue30};

    &:hover {
      background: ${theme.colors.blue40};
      border-bottom-color: ${theme.colors.blue20};
    }

    &:active {
      background: ${theme.colors.blue35};
    }
  `}

  ${({ dark, theme }) =>
    dark &&
    `
    background: ${theme.colors.grey50};
    border-color: ${theme.colors.grey40};
    border-bottom-color: ${theme.colors.grey30};
    color: white;

    &:hover {
      background: ${theme.colors.grey40};
    }

    &:active {
      background: ${theme.colors.grey30};
    }
  `}

  ${({ close, theme }) =>
    close &&
    `
    width: 2.5em;
    height: 2.5em;
    padding: 0;
    position: absolute;
    top: -1em;
    right: -1em;
    font-size: 1.6rem;
    border-radius: 50%;
    border-width: 0 0 1px 0;
    background-color: ${theme.colors.grey50};
    color: white;
    border-color: ${theme.colors.grey40};
    display: flex;
    align-items: center;
    justify-content: center;
    padding: .75em;

    &:hover {
      background-color: ${theme.colors.grey60};
      border-color: ${theme.colors.grey50};
    }
  `}
`;

export const BulletedNumber = styled.aside`
  text-align: center;
  position: relative;
  user-select: none;
  height: ${({ big }) => (big ? 27 : 22)}px;
  width: ${({ big }) => (big ? 27 : 22)}px;
  display: inline-block;

  ${({ inline, big }) =>
    inline &&
    `
    display: inline-block;
    width: ${big ? 27 : 22}px;
    margin: 4px 9px -4px 1px;
  `};

  span {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translateX(-50%) translateY(-50%);
    text-align: center;
    color: ${({ color }) => color || "white"};
    font-weight: 700;
    font-size: 12px;
    line-height: 1;
    letter-spacing: -0.5px;

    ${({ big }) =>
      big &&
      `
      font-size: 16px;
      letter-spacing: -1.5px;
      margin-left: -1px;
    `};
  }

  &:before {
    content: "";
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translateX(-50%) translateY(-50%);
    border-radius: 50%;
    height: 19px;
    width: 19px;
    background: ${({ theme, background }) => background || theme.colors.black};

    ${({ big, theme }) =>
      big &&
      `
      height: 27px;
      width: 27px;
    `};
  }
`;

export const DesktopOnly = styled.div`
  ${({ theme }) => theme.breakpoints.sm.down} {
    display: none;
  }
`;

export const MobileOnly = styled.div`
  ${({ theme }) => theme.breakpoints.sm.up} {
    display: none;
  }
`;
